import type { DoorFailure } from '#lib/auth/vitto-door';

/**
 * TASKS-APP-FULL-UI-1 U2b — what the person reads when the door does not open. DELIBERATE copy only: never a server body, never a
 * code on screen (the `code` picks the sentence). `retry`: asking for a NEW pass can help (a stale or spent pass, a busy host, nobody
 * answering); it cannot help when the answer is «this is not yours» (another organization, another app, an inactive account).
 */
export type DoorMessage = { title: string; detail: string; retry: boolean };

export function doorMessage(failure: DoorFailure): DoorMessage {
  if (failure.kind === 'host') {
    switch (failure.reason) {
      case 'not_allowed':
        return {
          title: 'Tasks is not allowed for you yet',
          detail: 'Allow Tasks in Vitto, then reopen it.',
          retry: false,
        };
      case 'app_not_installed':
        return {
          title: 'Tasks is not installed here',
          detail: 'Ask a workspace admin to install Tasks in this workspace.',
          retry: false,
        };
      case 'session_invalid':
        return {
          title: 'Your Vitto session could not be verified',
          detail: 'Reopen Tasks from Vitto.',
          retry: false,
        };
      case 'rate_limited':
        return { title: 'Vitto asked to wait a moment', detail: 'Try again shortly.', retry: true };
      case 'busy':
      case 'duplicate':
        return {
          title: 'Vitto is still working on the previous request',
          detail: 'Try again in a moment.',
          retry: true,
        };
      case 'timeout':
        return { title: 'Vitto did not answer in time', detail: 'Try again.', retry: true };
      default:
        return { title: 'Vitto could not be reached', detail: 'Try again.', retry: true };
    }
  }
  if (failure.kind === 'rejected') {
    switch (failure.code) {
      case 'installation_org_mismatch':
      case 'space_installation_mismatch':
        return {
          title: 'This Tasks belongs to another organization or Space',
          detail: 'Open the Tasks of your own Space from Vitto.',
          retry: false,
        };
      case 'user_inactive':
        return {
          title: 'Your account is inactive in Tasks',
          detail: 'Ask a workspace admin to reactivate it.',
          retry: false,
        };
      case 'app_mismatch':
      case 'audience_mismatch':
        return {
          title: 'This sign-in is for another app',
          detail: 'Reopen Tasks from Vitto.',
          retry: false,
        };
      case 'account_changed':
        return {
          title: 'This Tasks session now belongs to a different account',
          detail: 'Reopen Tasks from Vitto.',
          retry: false,
        };
      case 'vitto_bridge_not_configured':
        return {
          title: 'This Tasks is not connected to Vitto',
          detail: 'Its operator has to finish the setup.',
          retry: false,
        };
      case 'replayed':
      case 'expired':
        return {
          title: 'That sign-in has expired',
          detail: 'It was already used or took too long. Try again for a new one.',
          retry: true,
        };
      default:
        return {
          title: 'Tasks refused this sign-in',
          detail: 'Reopen Tasks from Vitto.',
          retry: false,
        };
    }
  }
  return { title: 'Tasks could not be reached', detail: 'Try again in a moment.', retry: true };
}
